import React, { useMemo, useRef } from 'react';

import { bricks } from '@opendoor/bricks/system';
import { Flex, Button, Card, Text, Image, Box, Icon } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';

import { googleStreetViewUrl } from 'buying/helpers/images';
import { PropertyCardImageContainer } from 'buying/components/propertyCards/PropertyCardImageContainer';
import { PropertyImageCarousel } from 'buying/components/propertyCards/PropertyImageCarousel';
import { useMlsPropertyCardEmitter } from 'buying/MlsComplianceContext';
import { formatApproxDuration, systemZonedDateTimeFromString } from 'shared/helpers/datetime';
import { Duration, ZonedDateTime } from '@js-joda/core';
import { PropertyCardFragment } from 'buying/graphql/fragments/__generated__/PropertyCardFragment';

export const MOBILE_PROPERTY_CARD_WIDTH = 280;
export const MIN_PROPERTY_CARD_HEIGHT = 245;

export interface PriceGuidancePropertyCardProps {
  property: PropertyCardFragment & { label?: string };
  carouselDisabled?: boolean;
  hoveredPropertyId?: string | null;
  onTouch?: (e?: React.TouchEvent<HTMLDivElement> | null) => void;
  onHover?: (e?: React.MouseEvent<HTMLDivElement> | null) => void;
  onClick?: () => void;
  comparisonProperty?: PropertyCardFragment | null;
}

/**
 * Generate an attribute differentiator for the property card.
 */
const generateAttributeCopy = (
  subjectAttribute: number,
  compAttribute: number,
  attributeName: string,
) => {
  if (compAttribute) {
    const diff = subjectAttribute - compAttribute;
    let copy;
    let icon;
    if (diff > 0) {
      icon = <Icon name="arrow-up" size={12} color="green50" />;
      copy = `${diff} more ${diff === 1 ? attributeName : `${attributeName}s`}`;
    } else if (diff < 0) {
      icon = <Icon name="arrow-down" size={12} color="red50" />;
      copy = `${-diff} less ${diff === -1 ? attributeName : `${attributeName}s`}`;
    } else {
      icon = <Icon name="menu" size={12} color="brand50" />;
      copy = `Same number of ${attributeName}s`;
    }

    return (
      <Text fontSize="s0" fontWeight="normal" color="neutrals100">
        {icon} {copy}
      </Text>
    );
  }
};

/**
 * Card with a summary of the home attribute differences compared to a subject property
 *
 * storybook-check-ignore
 */
export const PriceGuidancePropertyCard: React.FC<PriceGuidancePropertyCardProps> = ({
  property,
  carouselDisabled = false,
  hoveredPropertyId,
  onHover,
  onTouch,
  onClick,
  comparisonProperty,
}) => {
  const { isMobile } = useDevice();
  const imageContainer = useRef<HTMLDivElement | null>(null);
  const propertyPhotos = property?.photos || [];
  const photos = propertyPhotos
    .map((p) => p?.urls?.size768x512)
    .filter((photoUrl): photoUrl is string => !!photoUrl);

  useMlsPropertyCardEmitter(property.thumbnailLegalLogo);

  const duration = Duration.between(
    systemZonedDateTimeFromString(property.soldAt ?? property.publishedAt),
    ZonedDateTime.now(),
  );
  const formattedDuration = formatApproxDuration(duration).replace(/\b(minute|minutes)\b/g, 'min');
  const timeAgoFormatted = `${formattedDuration} ago`;

  const salePriceDiffCopy = useMemo(() => {
    const saleToListDiff = Math.round(
      ((property?.soldPrice ?? property.listPrice) / property.listPrice) * 100 - 100,
    );
    return `Sold ${saleToListDiff > 0 ? saleToListDiff : -saleToListDiff}% ${
      saleToListDiff > 0 ? 'over' : 'under'
    } list, ${timeAgoFormatted}`;
  }, [property.listPrice, property.soldPrice, timeAgoFormatted]);

  const bedroomCallout = useMemo(() => {
    return comparisonProperty?.bedrooms
      ? generateAttributeCopy(property.bedrooms, comparisonProperty.bedrooms, 'bedroom')
      : null;
  }, [comparisonProperty, property.bedrooms]);

  const bathroomCallout = useMemo(() => {
    return comparisonProperty?.bathroomsTotal
      ? generateAttributeCopy(
          property.bathroomsTotal,
          comparisonProperty.bathroomsTotal,
          'bathroom',
        )
      : null;
  }, [comparisonProperty, property.bathroomsTotal]);

  const propertyAgeCallout = useMemo(() => {
    if (comparisonProperty?.yearBuilt) {
      const diff = property.yearBuilt - comparisonProperty.yearBuilt;
      let copy;
      let icon;
      if (diff > 0) {
        icon = <Icon name="arrow-up" size={12} color="green50" />;
        copy = `${diff} years younger`;
      } else if (diff < 0) {
        icon = <Icon name="arrow-down" size={12} color="red50" />;
        copy = `${-diff} years older`;
      } else {
        icon = <Icon name="menu" size={12} color="brand50" />;
        copy = 'Same year';
      }

      return (
        <Text fontSize="s0" fontWeight="normal" color="neutrals100">
          {icon} {copy}
        </Text>
      );
    }
  }, [comparisonProperty?.yearBuilt, property.yearBuilt]);

  const handleOnMouseLeave = () => {
    if (onHover) {
      onHover(null);
    }
  };

  return (
    <Card
      data-propertyid={property.id}
      width={isMobile ? MOBILE_PROPERTY_CARD_WIDTH : '100%'}
      variant={
        hoveredPropertyId
          ? hoveredPropertyId === property.id
            ? 'secondary'
            : 'tertiary'
          : 'tertiary'
      }
      height="100%"
      padding={0}
      _hover={{
        boxShadow: 'z2',
      }}
      transition="box-shadow 0.2s ease-in-out"
      onTouchStart={onTouch}
      onMouseEnter={onHover}
      onMouseLeave={handleOnMouseLeave}
      id={`price-guidance-property-card-${property.id}`}
    >
      <Flex
        flexDirection="column"
        height="100%"
        ref={imageContainer}
        position="relative"
        flexGrow={0}
      >
        <PropertyCardImageContainer>
          {!carouselDisabled && photos.length >= 2 && (
            <PropertyImageCarousel
              photos={photos}
              photoCount={photos.length}
              propertyAddress={`${property.street} ${property.city} ${property.state}`}
            />
          )}

          {(carouselDisabled || photos.length < 2) && (
            <Image
              alt="default property photo"
              src={
                photos.length > 0
                  ? photos[0]
                  : googleStreetViewUrl({
                      type: 'address',
                      street: property.street,
                      city: property.city,
                      state: property.state,
                    })
              }
              width="100%"
              height="100%"
              objectFit="cover"
            />
          )}
        </PropertyCardImageContainer>
        <Flex color="neutrals100" justifyContent="space-between" padding={5}>
          <Flex flexDirection="column">
            <Flex alignItems="center" flexDirection="row" flexWrap="wrap">
              <Text size="s1" mr={3} fontWeight="semibold">
                {property.soldPrice && `Sold for $${property.soldPrice.toLocaleString()}`}
              </Text>
              <Text size="s00" color="neutrals90">
                List ${property.listPrice.toLocaleString()}
              </Text>
            </Flex>
            <Text size="s00" color="neutrals90">
              {property.bedrooms ?? 0} bed {property.bathroomsTotal} bath ·{' '}
              {property.totalLivingSqFt} ft
              <bricks.sup>2</bricks.sup>
            </Text>
            <Text size="s00" color="neutrals90" textOverflow="ellipsis" noOfLines={1}>
              {salePriceDiffCopy}
            </Text>
            {bedroomCallout}
            {bathroomCallout}
            {propertyAgeCallout}
            {onClick && (
              <Button
                type="button"
                p="unset"
                analyticsName="cake-open-price-guidance-comparison-modal"
                aria-hidden
                aria-label=""
                variant="link"
                onClick={onClick}
                fontSize="s0"
              >
                See how this home compares <Icon name="chevron-right" size={12} color="brand50" />
              </Button>
            )}
          </Flex>
        </Flex>
        <Box position="absolute" top="4" left="4">
          <Flex
            backgroundColor="neutrals0"
            border="2px solid"
            borderColor="neutrals20"
            height="28px"
            flexDirection="row-reverse"
            borderRadius="roundedSquare"
            justifyContent="center"
            alignItems="center"
            py="3"
            px="3"
            mb="3"
          >
            <Text
              fontSize="s00"
              lineHeight="s000"
              fontWeight="semibold"
              data-testid="badge-text"
              alignSelf="center"
            >
              {hoveredPropertyId === property.id
                ? `${property?.label} ${property.street}` || 'Comp'
                : property?.label || 'Comp'}
            </Text>
          </Flex>
        </Box>
      </Flex>
    </Card>
  );
};
